import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@appic/lib/supabase'

/**
 * Subscribes to Supabase Realtime changes on published tables and
 * invalidates the corresponding React Query caches.
 *
 * Mount once in App.tsx / AppLayout. No state — just cache invalidation.
 *
 * Published tables (configured in Supabase):
 *   appointments, contact_persons, joker_stops,
 *   mission_leads, missions, visit_reports_v2
 */

interface TableInvalidation {
  table: string
  queryKeys: string[][]
}

const TABLE_INVALIDATIONS: TableInvalidation[] = [
  {
    table: 'missions',
    queryKeys: [['missions-overview']],
  },
  {
    table: 'mission_leads',
    queryKeys: [['missions-overview'], ['mission-lead-bundle'], ['prequal-leads'], ['prequal-results-today']],
  },
  {
    table: 'appointments',
    queryKeys: [['scout-week']],
  },
  {
    table: 'contact_persons',
    queryKeys: [['mission-lead-bundle']],
  },
  {
    table: 'joker_stops',
    queryKeys: [['joker-stops'], ['joker-count-today']],
  },
  {
    table: 'visit_reports_v2',
    queryKeys: [['missions-overview'], ['mission-lead-bundle'], ['prequal-results-today']],
  },
  {
    table: 'sprints',
    queryKeys: [['sprint-detail']],
  },
]

export type RealtimeConnectionStatus = 'connected' | 'reconnecting' | 'disconnected'

export interface RealtimeSyncResult {
  /** Current Supabase Realtime channel connection status */
  status: RealtimeConnectionStatus
}

/**
 * Every query key the realtime channel keeps fresh — derived from
 * TABLE_INVALIDATIONS (never a second hand-kept list), deduplicated.
 */
export const REALTIME_INVALIDATION_QUERY_KEYS: readonly (readonly string[])[] = (() => {
  const seen = new Map<string, string[]>()
  for (const { queryKeys } of TABLE_INVALIDATIONS) {
    for (const key of queryKeys) seen.set(JSON.stringify(key), key)
  }
  return [...seen.values()]
})()

/** The slice of QueryClient this module needs — injectable for tests. */
export interface QueryInvalidator {
  invalidateQueries: (filters: { queryKey: readonly string[] }) => Promise<void> | void
}

/**
 * Re-fetch everything the realtime channel would have refreshed. Called after a
 * RECONNECT: while the socket was down, change events were missed, and nothing
 * else would refresh those caches until their own stale timers fire.
 */
export function invalidateRealtimeQueries(qc: QueryInvalidator): void {
  for (const queryKey of REALTIME_INVALIDATION_QUERY_KEYS) {
    void qc.invalidateQueries({ queryKey })
  }
}

/** Channel lifecycle as the resync decision needs it. */
export interface ResyncState {
  /** The channel has been SUBSCRIBED at least once. */
  connectedOnce: boolean
  /** A loss (error / timeout / close) happened after the first subscribe. */
  lostSinceConnected: boolean
}

export const INITIAL_RESYNC_STATE: ResyncState = { connectedOnce: false, lostSinceConnected: false }

/**
 * Fold one channel status into the resync state. `resync` is true exactly on a
 * SUBSCRIBED that follows a loss after an earlier subscribe — the first
 * subscribe at boot is not a reconnect (the queries just loaded).
 */
export function nextResync(state: ResyncState, channelStatus: string): { state: ResyncState; resync: boolean } {
  if (channelStatus === 'SUBSCRIBED') {
    return { state: { connectedOnce: true, lostSinceConnected: false }, resync: state.lostSinceConnected }
  }
  if (channelStatus === 'CHANNEL_ERROR' || channelStatus === 'TIMED_OUT' || channelStatus === 'CLOSED') {
    return { state: { ...state, lostSinceConnected: state.connectedOnce }, resync: false }
  }
  return { state, resync: false }
}

export function useRealtimeSync(): RealtimeSyncResult {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<RealtimeConnectionStatus>('disconnected')

  useEffect(() => {
    if (typeof supabase.channel !== 'function') {
      setStatus('disconnected')
      return
    }
    const channel = supabase.channel('db-sync')
    let resyncState = INITIAL_RESYNC_STATE

    for (const { table, queryKeys } of TABLE_INVALIDATIONS) {
      channel.on(
        'postgres_changes' as any,
        { event: '*', schema: 'public', table },
        () => {
          for (const queryKey of queryKeys) {
            queryClient.invalidateQueries({ queryKey })
          }
        }
      )
    }

    channel.subscribe((status) => {
      const step = nextResync(resyncState, status)
      resyncState = step.state
      if (step.resync) invalidateRealtimeQueries(queryClient)
      if (status === 'SUBSCRIBED') {
        setStatus('connected')
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        setStatus('reconnecting')
      } else if (status === 'CLOSED') {
        setStatus('disconnected')
      }
    })

    return () => {
      supabase.removeChannel(channel)
      setStatus('disconnected')
    }
  }, [queryClient])

  return { status }
}
